import type { ButtonHTMLAttributes, ReactNode } from "react"

import { Slot } from "@radix-ui/react-slot"

import { tv, type VariantProps } from "@/lib/utils/tv"

const button = tv({
  base: "hit-target press inline-flex max-w-full cursor-pointer select-none items-center justify-center gap-200 font-display font-semibold whitespace-nowrap focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2",
  variants: {
    variant: {
      primary: "bg-brand text-fg-on-brand hover:bg-brand-hover active:bg-brand-pressed",
      secondary: "border-field border-line-brand bg-surface text-fg-brand hover:bg-brand-subtle",
      ghost: "bg-transparent text-fg-brand hover:bg-brand-subtle",
      danger: "bg-surface-danger text-fg-inverted hover:bg-danger-hover",
      inverse: "bg-surface text-palette-ink hover:bg-surface-subtle",
      // Editorial bands only. The reference keeps blue scarce: the hero's CTA is
      // the page's one blue action, and every band beneath it sits in ink.
      ink: "bg-surface-ink text-fg-on-inverse hover:bg-surface-ink/88 active:bg-surface-ink/80",
      muted: "bg-line text-fg-heading hover:bg-line active:bg-line",
      outline: "border-field border-line-subtle bg-surface text-fg hover:border-line-brand hover:bg-brand-subtle hover:text-fg-brand",
      quiet: "h-auto min-w-0 bg-transparent p-0 text-fg-brand hover:text-brand-hover",
      whatsapp: "bg-surface-success text-fg-on-paper hover:bg-surface-success/85 active:bg-surface-success/70",
    },
    size: {
      lg: "h-12 min-w-0 rounded-full px-800 text-body-lg @3xl:h-13",
      md: "h-12 min-w-0 @sm:min-w-50 rounded-full px-600 text-control",
      sm: "h-9 min-w-30 rounded-full px-400 text-body-sm",
    },
    fullWidth: { true: "w-full" },
    loading: { true: "" },
    // Disabled/loading visuals as an explicit variant (not :disabled pseudo)
    // so the asChild branch — where no disabled attribute exists — looks and
    // behaves inert too. pointer-events-none is the click blocker for links.
    inactive: { true: "pointer-events-none cursor-not-allowed border-transparent bg-line text-fg-inverted shadow-none" },
  },
  compoundVariants: [
    { variant: "quiet", class: "h-auto min-w-0 @sm:min-w-0 px-0 @3xl:h-auto" },
    { variant: "primary", loading: true, class: "bg-brand" },
    { variant: "danger", loading: true, class: "bg-surface-danger" },
    { variant: "whatsapp", loading: true, class: "bg-surface-success" },
    { variant: "secondary", loading: true, class: "border-line-brand bg-surface text-fg-brand" },
    { variant: "ghost", loading: true, class: "bg-transparent text-fg-brand" },
  ],
  defaultVariants: { variant: "primary", size: "lg" },
})

const ButtonDots = () => (
  <span className="inline-flex items-center gap-100" aria-hidden>
    <span className="motion-safe:animate-dots size-1.5 rounded-full bg-current" />
    <span className="motion-safe:animate-dots animation-delay-300 size-1.5 rounded-full bg-current" />
    <span className="motion-safe:animate-dots animation-delay-600 size-1.5 rounded-full bg-current" />
  </span>
)

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement>, Omit<VariantProps<typeof button>, "inactive"> {
  loading?: boolean
  iconLeft?: ReactNode
  iconRight?: ReactNode
  asChild?: boolean
}

export const Button = ({
  variant,
  size,
  fullWidth,
  loading = false,
  iconLeft,
  iconRight,
  disabled,
  type = "button",
  className,
  children,
  asChild = false,
  onClick,
  ...rest
}: ButtonProps) => {
  const inactive = disabled || loading
  const classes = button({ variant, size, fullWidth, loading, inactive, className })
  if (asChild) {
    // Slot runs the CHILD's onClick before ours, so preventDefault here can't
    // stop a Link navigation — pointer-events-none (mouse/touch) plus
    // tabIndex -1 (keyboard) do the real blocking; the guard only covers
    // programmatic .click().
    return (
      <Slot
        className={classes}
        aria-disabled={inactive || undefined}
        aria-busy={loading || undefined}
        tabIndex={inactive ? -1 : undefined}
        onClick={inactive ? (event) => event.preventDefault() : onClick}
        {...rest}
      >
        {children}
      </Slot>
    )
  }
  return (
    <button type={type} disabled={inactive} aria-busy={loading || undefined} className={classes} onClick={onClick} {...rest}>
      {loading ? (
        <ButtonDots />
      ) : (
        <>
          {iconLeft}
          {children}
          {iconRight}
        </>
      )}
    </button>
  )
}
